import { observable, reaction, runInAction } from "mobx"

import { ProjectGitHubIntegrationModel } from "@/components/ProjectSettingsDialog/ProjectGitHubIntegrationModel.ts"
import { PromptInputModelImpl } from "@/components/PromptInput/PromptInputModel.ts"
import { type RepositoryAuthType } from "@/lib/client/generated/TypeDefinitions.ts"
import { Model } from "@/lib/Model.ts"

import type { PR_Project2, RepositoryFetchStatus } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"


export class ProjectMirroringModel extends Model {
  readonly status: IObservableValue<RepositoryFetchStatus | null> = observable.box(null)
  readonly authOptions: IObservableValue<RepositoryAuthType[]> = observable.box([])
  readonly integrationModel: ProjectGitHubIntegrationModel
  private fetchStatusInterval: ReturnType<typeof setInterval> | null = null

  readonly isDragging = observable.box(false)
  readonly isSaving = observable.box(false)

  constructor(
    readonly context: OrgContext,
    readonly project: PR_Project2,
    readonly queries: Queries,
    readonly canEditProject: IObservableValue<boolean>
  ) {
    super("ProjectMirroringModel")

    this.integrationModel = new ProjectGitHubIntegrationModel(context, project)

    void this.fetchStatus()

    // React to the first non-null remoteUrl and fetch auth options
    let authFetched = false
    const disposeRemoteUrlReaction = reaction(
      () => this.status.get()?.remoteUrl ?? null,
      (remoteUrl) => {
        if (remoteUrl && !authFetched) {
          authFetched = true
          void this.fetchRepositoryAuthOptions(remoteUrl)
        }
      },
      { fireImmediately: true }
    )
    this.lifetime.add(disposeRemoteUrlReaction, "ProjectMirroringModel:remoteUrlReaction")

    // Poll fetch status every 10 seconds
    this.fetchStatusInterval = setInterval(() => {
      void this.fetchStatus()
    }, 10_000)
  }

  async fetchStatus() {
    runInAction(() => this.isLoading.set(true))

    try {
      const status = await this.queries.getFetchStatus(this.project.key)
      runInAction(() => this.status.set(status))
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }

  async fetchRepositoryAuthOptions(remoteUrl: string) {
    const options = await this.queries.getRepositoryAuthOptions(remoteUrl)
    runInAction(() => this.authOptions.set(options))
  }

  async setRepositoryToken(remoteUrl: string, token: string) {
    runInAction(() => this.isSaving.set(true))
    try {
      await this.queries.apiClient.updateRepositoryAuth(this.project.key, this.project.repository.name, remoteUrl, {
        className: "Token",
        token: token
      })
      await this.startFetch()
    } finally {
      runInAction(() => this.isSaving.set(false))
    }
  }

  async setRepositorySshKey(remoteUrl: string, key: string, password: string | null) {
    runInAction(() => this.isSaving.set(true))
    try {
      await this.queries.apiClient.updateRepositoryAuth(this.project.key, this.project.repository.name, remoteUrl, {
        className: "SSH",
        privateKey: key,
        passphrase: password ?? ""
      })
      await this.startFetch()
    } finally {
      runInAction(() => this.isSaving.set(false))
    }
  }

  async onSshKeyUploaded(file: File, setValue: (text: string) => void) {
    try {
      const value = await PromptInputModelImpl.readSshPrivateKeyFile(file)
      runInAction(() => setValue(value))
    } catch {
      // toast already shown by helper
    }
  }

  async startFetch() {
    await this.queries.apiClient.startFetch(this.project.key)

    // Optimistically update the local status to "Running" immediately after starting fetch
    runInAction(() => {
      const prev = this.status.get()
      if (!prev) return

      const next: RepositoryFetchStatus = {
        lastSync: prev.lastSync,
        lines: prev.lines,
        remoteUrl: prev.remoteUrl,
        getTokenUrl: prev.getTokenUrl,
        status: "Running",
        authError: false,
        topLine: prev.topLine,
      }
      this.status.set(next)
    })

    // Still trigger a server refresh after 1s to reconcile with the actual state
    setTimeout(() => {
      void this.fetchStatus()
    }, 1000)
  }

  terminate() {
    super.terminate()
    this.integrationModel.terminate()

    if (this.fetchStatusInterval !== null) {
      clearInterval(this.fetchStatusInterval)
      this.fetchStatusInterval = null
    }
  }
}
